export type ExpressQuoteAvailability = {
  isAvailable: boolean;
  stockCode: string | null;
};

export type ExpressQuoteResponse = {
  inRange?: boolean;
  inRangeStocks?: string[];
};

/**
 * Decide whether an express/quote response means express is genuinely available,
 * and which stock code backs it.
 *
 * Why this exists (HL-9096): the express/quote backend treats a missing
 * selectedStockCode as "quote every eligible stock" and returns inRange:true
 * whenever any in-range stock exists. So a next-day-eligible zip is reported
 * available before the cart pins an express stock. The Shipping page used to gate
 * on its own local expressStockCode (`quoteData.inRange && expressStockCode`),
 * which is null in exactly that window — so it rejected a valid quote and fired a
 * false "express not available" notification, reverting the customer to DHL.
 *
 * Trust the backend's inRange verdict; derive the backing stock from the freshest
 * source available rather than the possibly-stale local code.
 *
 * @param quoteData          the express/quote response (inRange + inRangeStocks)
 * @param responseStockCode  expressStockCode from the cartView in this response (freshest)
 * @param currentStockCode   the page's current local expressStockCode (possibly stale/null)
 */
export const resolveExpressQuoteAvailability = (
  quoteData: ExpressQuoteResponse,
  responseStockCode: string | null | undefined,
  currentStockCode: string | null | undefined,
): ExpressQuoteAvailability => {
  if (!quoteData.inRange) {
    return { isAvailable: false, stockCode: null };
  }
  // Backend-first: the response code is freshest; inRangeStocks is what the backend just
  // confirmed reachable for the current zip; the local code is last (it may be for the
  // pre-change zip's pharmacy and invalid here). ?? not || so an empty-string code is kept.
  const stockCode = responseStockCode ?? quoteData.inRangeStocks?.[0] ?? currentStockCode ?? null;
  return { isAvailable: true, stockCode };
};
